import { useCallback } from 'react'
import { useClient } from '../../graphql/provider'
import useGlobalStore from '../../stores/globalStore'
import useCalendarUIStore from '../../views/Calendar/useCalendarUIStore'
import { toEntryDate } from '../../server/utils/date'
import * as timeEntriesCache from './timeEntriesCache'
import * as timeEntryService from '../../entities/timeEntry/service'
import {
  transformDataToValidEntry,
  resolveTimesheetsByDay,
  dayKeyForEntry,
} from '../../server/actions/helpers/timeEntryActionHelper'
import { addEventDataToEntry } from '../../server/funnel/funnelUtils'
import eventUtils from '../../utils/eventUtils'
import { validateHexString } from '../../utils/validateHexString'
import { logError } from '../../utils/logger'

const BATCH_CONCURRENCY = 4

const runWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length)
  let cursor = 0

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor
      cursor += 1
      results[index] = await worker(items[index])
    }
  })

  await Promise.all(runners)
  return results
}

const parseEntry = timeEntry =>
  eventUtils.parseLocalEntryDatesAndSource(addEventDataToEntry(timeEntry))

const useTimeEntryMutations = ({ clearCurrentEditableEntry: clearFromProps } = {}) => {
  const client = useClient()
  const { selectedDelegateId } = useGlobalStore()
  const clearFromStore = useCalendarUIStore(s => s.clearCurrentEditableEntry)
  const addPendingEntryId = useCalendarUIStore(s => s.addPendingEntryId)
  const removePendingEntryId = useCalendarUIStore(s => s.removePendingEntryId)
  const clearCurrentEditableEntry = clearFromProps ?? clearFromStore

  const buildEntry = useCallback(
    async (entryData, timesheet) => {
      const originalEntryDate = entryData.originalEntryDate ?? toEntryDate(entryData.start)
      return transformDataToValidEntry({
        client,
        eventData: { ...entryData, originalEntryDate },
        selectedDelegateId,
        timesheet,
      })
    },
    [client, selectedDelegateId],
  )

  const createEntry = useCallback(
    async entryData => {
      const provisionalId = `provisional-${Date.now()}`
      const provisionalEntry = parseEntry({
        ...entryData,
        _id: provisionalId,
        originalEntryDate: toEntryDate(entryData.originalEntryDate ?? entryData.start),
      })

      timeEntriesCache.cancelPending()
      timeEntriesCache.addEntry(provisionalEntry)
      addPendingEntryId(provisionalId)
      clearCurrentEditableEntry()

      try {
        const entry = await buildEntry(entryData)
        const newTimeEntry = await timeEntryService.createTimeEntry({
          client,
          timeEntry: entry,
          selectedDelegateId,
        })
        timeEntriesCache.cancelPending()
        timeEntriesCache.replaceEntry(provisionalId, parseEntry(newTimeEntry))
        removePendingEntryId(provisionalId)
        timeEntriesCache.invalidate()
        return parseEntry(newTimeEntry)
      } catch (e) {
        timeEntriesCache.removeEntry(provisionalId)
        removePendingEntryId(provisionalId)
        logError(e, 'useTimeEntryMutations > createEntry')
        throw e
      }
    },
    [
      buildEntry,
      client,
      selectedDelegateId,
      clearCurrentEditableEntry,
      addPendingEntryId,
      removePendingEntryId,
    ],
  )

  const createEntries = useCallback(
    async entriesData => {
      if (!entriesData.length) return { created: [], failed: [] }

      clearCurrentEditableEntry()

      const timesheetsByDay = await resolveTimesheetsByDay({
        client,
        entriesData,
        selectedDelegateId,
      })

      const outcomes = await runWithConcurrency(entriesData, BATCH_CONCURRENCY, async entryData => {
        const dayKey = dayKeyForEntry(entryData)
        if (!timesheetsByDay.has(dayKey)) return { ok: false, input: entryData }

        try {
          const entry = await buildEntry(entryData, timesheetsByDay.get(dayKey))
          const newTimeEntry = await timeEntryService.createTimeEntry({
            client,
            timeEntry: entry,
            selectedDelegateId,
          })
          return { ok: true, entry: parseEntry(newTimeEntry) }
        } catch (e) {
          logError(e, 'useTimeEntryMutations > createEntries')
          return { ok: false, input: entryData }
        }
      })

      timeEntriesCache.cancelPending()
      const created = outcomes.filter(o => o.ok).map(o => o.entry)
      const failed = outcomes.filter(o => !o.ok).map(o => o.input)

      await timeEntriesCache.invalidate()

      return { created, failed }
    },
    [client, selectedDelegateId, buildEntry, clearCurrentEditableEntry],
  )

  const updateEntry = useCallback(
    async updatedEntry => {
      const entryId = updatedEntry._id
      const approveState = updatedEntry.approveState || {}

      const previousEntry = timeEntriesCache.findEntry(entryId)

      timeEntriesCache.cancelPending()
      timeEntriesCache.patchEntry(entryId, updatedEntry)
      addPendingEntryId(entryId)
      clearCurrentEditableEntry()

      try {
        await timeEntryService.updateTimeEntry({
          client,
          timeEntry: {
            ...updatedEntry,
            approveState: {
              status: approveState.status,
              rejectionReason: approveState.rejectionReason,
              modifiedBy: approveState.modifiedBy,
            },
          },
          selectedDelegateId,
        })
        removePendingEntryId(entryId)
        await timeEntriesCache.invalidate()
      } catch (e) {
        timeEntriesCache.restoreEntry(entryId, previousEntry)
        removePendingEntryId(entryId)
        logError(e, 'useTimeEntryMutations > updateEntry')
        throw e
      }
    },
    [
      client,
      selectedDelegateId,
      clearCurrentEditableEntry,
      addPendingEntryId,
      removePendingEntryId,
    ],
  )

  const deleteEntry = useCallback(
    async entry => {
      try {
        if (validateHexString(entry._id)) {
          await timeEntryService.deleteTimeEntry({ client, timeEntry: entry, selectedDelegateId })
        }
        await timeEntriesCache.invalidateAfterDelete()
      } catch (e) {
        logError(e, 'useTimeEntryMutations > deleteEntry')
        throw e
      }
    },
    [client, selectedDelegateId],
  )

  return { createEntry, createEntries, updateEntry, deleteEntry }
}

export default useTimeEntryMutations
